import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  isSameDay,
  startOfDay,
} from 'date-fns';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  CakeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import { Link } from 'react-router-dom';
import { calendarApi, CalendarEvent, CalendarEventCreate, CalendarEventUpdate, SpecialDateEvent } from '../../api/calendar';
import EventDetail from '../../components/calendar/EventDetail';
import EventForm from '../../components/calendar/EventForm';
import { useMarkCategoryRead } from '../../hooks/useMarkCategoryRead';

// Ring icon for anniversaries
const RingIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="6" />
    <path d="M12 2v4" />
    <path d="M9 3l3 3 3-3" />
  </svg>
);

// Icons and colors for special date types
const specialDateConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; bgColor: string; emoji: string }> = {
  birthday: { icon: CakeIcon, color: 'text-pink-400', bgColor: 'bg-pink-500', emoji: '🎂' },
  nameday: { icon: CalendarIcon, color: 'text-purple-400', bgColor: 'bg-purple-500', emoji: '📅' },
  anniversary: { icon: RingIcon, color: 'text-amber-400', bgColor: 'bg-amber-500', emoji: '💍' },
  wedding: { icon: RingIcon, color: 'text-amber-400', bgColor: 'bg-amber-500', emoji: '💒' },
  other: { icon: SparklesIcon, color: 'text-blue-400', bgColor: 'bg-blue-500', emoji: '✨' },
};

// Combined event type for display
type DisplayEvent = CalendarEvent | SpecialDateEvent;

export default function Calendar() {
  const { currentFamily } = useFamilyStore();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [specialDates, setSpecialDates] = useState<SpecialDateEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Mark calendar notifications as read when visiting this page
  useMarkCategoryRead('calendar');
  const wsRef = useRef<WebSocket | null>(null);

  // Combine regular events with special dates
  const allEvents: DisplayEvent[] = [...events, ...specialDates];

  // Fetch events when family or month changes
  useEffect(() => {
    if (!currentFamily) return;

    const fetchEvents = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        // Fetch only events for the current month being displayed
        const startDate = monthStart;
        const endDate = monthEnd;
        const year = currentMonth.getFullYear();

        // Fetch both regular events and special dates in parallel
        const [fetchedEvents, fetchedSpecialDates] = await Promise.all([
          calendarApi.getFamilyEvents(currentFamily.id, startDate, endDate),
          calendarApi.getSpecialDates(currentFamily.id, year),
        ]);
        
        setEvents(fetchedEvents);
        setSpecialDates(fetchedSpecialDates);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load events');
        console.error('Error fetching events:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [currentFamily, currentMonth]);

  // Websocket for realtime calendar updates
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!currentFamily?.id || !token) return;

    const base = (import.meta.env.VITE_API_URL || '').replace(/^http/, 'ws');
    if (!base) return;

    const socket = new WebSocket(`${base}/ws/families/${currentFamily.id}?token=${token}`);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        
        if (payload.type === 'calendar_event_created') {
          const newEvent = payload.data;
          // Check if event is in current month
          const eventDate = new Date(newEvent.start_datetime);
          if (isSameMonth(eventDate, currentMonth)) {
            setEvents((old) => {
              const exists = old.find((e) => e.id === newEvent.id);
              if (exists) return old;
              return [...old, newEvent];
            });
          }
        }
        
        if (payload.type === 'calendar_event_updated') {
          const updatedEvent = payload.data;
          const eventDate = new Date(updatedEvent.start_datetime);
          // If event moved to/from current month, refetch
          setEvents((old) => {
            const exists = old.find((e) => e.id === updatedEvent.id);
            if (exists && isSameMonth(eventDate, currentMonth)) {
              return old.map((e) => (e.id === updatedEvent.id ? updatedEvent : e));
            } else if (exists && !isSameMonth(eventDate, currentMonth)) {
              // Event moved out of current month
              return old.filter((e) => e.id !== updatedEvent.id);
            } else if (!exists && isSameMonth(eventDate, currentMonth)) {
              // Event moved into current month
              return [...old, updatedEvent];
            }
            return old;
          });
        }
        
        if (payload.type === 'calendar_event_deleted') {
          setEvents((old) => old.filter((e) => e.id !== payload.data.id));
        }
      } catch {
        // ignore malformed events
      }
    };

    socket.onclose = () => {
      wsRef.current = null;
    };

    return () => {
      socket.close();
      wsRef.current = null;
    };
  }, [currentFamily?.id, currentMonth]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad to start on Sunday
  const startDay = monthStart.getDay();
  const paddedDays: (Date | null)[] = [...Array(startDay).fill(null), ...days];

  const getEventsForDay = (date: Date): DisplayEvent[] => {
    const dayStart = startOfDay(date);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);

    return allEvents.filter((event) => {
      const eventStart = new Date(event.start_datetime);
      const eventEnd = new Date(event.end_datetime);
      
      // Check if event overlaps with the day
      return eventStart <= dayEnd && eventEnd >= dayStart;
    });
  };

  const isSpecialDateEvent = (event: DisplayEvent): event is SpecialDateEvent => {
    return 'is_special_date' in event && event.is_special_date === true;
  };

  const handleCreateEvent = async (data: CalendarEventCreate | CalendarEventUpdate) => {
    if (!currentFamily) return;
    
    const newEvent = await calendarApi.createEvent(currentFamily.id, data as CalendarEventCreate);
    setEvents([...events, newEvent]);
    setShowEventForm(false);
  };

  const handleUpdateEvent = async (data: CalendarEventCreate | CalendarEventUpdate) => {
    if (!currentFamily || !editingEvent) return;
    
    const updatedEvent = await calendarApi.updateEvent(
      currentFamily.id,
      editingEvent.id,
      data as CalendarEventUpdate
    );
    setEvents(events.map((e) => (e.id === updatedEvent.id ? updatedEvent : e)));
    setEditingEvent(null);
    setShowEventForm(false);
    setSelectedEvent(null);
  };

  const handleDeleteEvent = async () => {
    if (!currentFamily || !selectedEvent) return;
    
    if (window.confirm('Are you sure you want to delete this event?')) {
      await calendarApi.deleteEvent(currentFamily.id, selectedEvent.id);
      setEvents(events.filter((e) => e.id !== selectedEvent.id));
      setSelectedEvent(null);
    }
  };

  const handleEditEvent = () => {
    if (!selectedEvent) return;
    setEditingEvent(selectedEvent);
    setSelectedEvent(null);
    setShowEventForm(true);
  };

  if (!currentFamily) {
    return (
      <div className="page-content">
        <div className="card text-center py-16">
          <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-surface-500" />
          <h2 className="text-xl font-bold text-white mb-2">No Family Selected</h2>
          <p className="text-surface-400 mb-6">Select or create a family to view calendar</p>
          <Link to="/families" className="btn-primary inline-flex">
            Go to Families
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Calendar</h1>
          <p className="text-surface-400">{currentFamily.name}</p>
        </div>
        <button
          onClick={() => {
            setEditingEvent(null);
            setShowEventForm(true);
          }}
          className="btn-primary"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Add Event</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="btn-icon"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-semibold text-white">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="btn-icon"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="card">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="text-center text-sm font-medium text-surface-400 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        {isLoading ? (
          <div className="py-12 text-center text-surface-400">Loading events...</div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {paddedDays.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const dayEvents = getEventsForDay(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);

              return (
                <motion.button
                  key={day.toISOString()}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedDate(day)}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center transition-all ${
                    isToday(day)
                      ? 'bg-primary-500/20 text-primary-300'
                      : isSelected
                      ? 'bg-white/10 text-white'
                      : isSameMonth(day, currentMonth)
                      ? 'text-white hover:bg-white/5'
                      : 'text-surface-500'
                  }`}
                >
                  <span className="text-sm font-medium">{format(day, 'd')}</span>
                  {dayEvents.length > 0 && (
                    <div className="flex gap-0.5 mt-1 items-center">
                      {dayEvents.slice(0, 3).map((event) => {
                        const isSpecial = isSpecialDateEvent(event);
                        if (isSpecial) {
                          const config = specialDateConfig[(event as SpecialDateEvent).date_type] || specialDateConfig.other;
                          const IconComponent = config.icon;
                          return (
                            <IconComponent
                              key={event.id}
                              className={`w-3 h-3 ${config.color}`}
                            />
                          );
                        }
                        return (
                          <div
                            key={event.id}
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: event.color }}
                          />
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <span className="text-xs text-surface-400">+{dayEvents.length - 3}</span>
                      )}
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Date Events */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4">
            {format(selectedDate, 'EEEE, MMMM d')}
          </h3>
          <div className="card">
            {getEventsForDay(selectedDate).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-surface-400 mb-4">No events scheduled</p>
                <button
                  onClick={() => {
                    setEditingEvent(null);
                    setShowEventForm(true);
                  }}
                  className="btn-secondary text-sm"
                >
                  Add Event
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {getEventsForDay(selectedDate).map((event) => {
                  const isSpecial = isSpecialDateEvent(event);
                  const specialConfig = isSpecial 
                    ? specialDateConfig[(event as SpecialDateEvent).date_type] || specialDateConfig.other 
                    : null;
                  const SpecialIcon = specialConfig?.icon;
                  
                  return (
                    <button
                      key={event.id}
                      onClick={() => {
                        // Only open detail modal for regular events
                        if (!isSpecial) {
                          setSelectedEvent(event as CalendarEvent);
                        }
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${
                        isSpecial 
                          ? 'bg-gradient-to-r from-pink-500/10 to-amber-500/10 border border-pink-500/20' 
                          : 'bg-surface-800/50 hover:bg-surface-800'
                      }`}
                    >
                      {isSpecial && SpecialIcon ? (
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${specialConfig?.bgColor}/20`}>
                          <SpecialIcon className={`w-5 h-5 ${specialConfig?.color}`} />
                        </div>
                      ) : (
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: event.color }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium truncate">{event.name}</p>
                          {isSpecial && specialConfig && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${specialConfig.bgColor}/20 ${specialConfig.color} shrink-0`}>
                              {specialConfig.emoji}
                            </span>
                          )}
                        </div>
                        {isSpecial ? (
                          <p className="text-sm text-surface-400">
                            {(event as SpecialDateEvent).person_name}
                          </p>
                        ) : (
                          !event.all_day && (
                            <p className="text-sm text-surface-400">
                              {format(new Date(event.start_datetime), 'h:mm a')}
                              {event.end_datetime &&
                                ` - ${format(new Date(event.end_datetime), 'h:mm a')}`}
                            </p>
                          )
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetail
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onEdit={handleEditEvent}
          onDelete={handleDeleteEvent}
        />
      )}

      {/* Event Form Modal */}
      {showEventForm && (
        <EventForm
          event={editingEvent}
          initialDate={selectedDate || undefined}
          onClose={() => {
            setShowEventForm(false);
            setEditingEvent(null);
          }}
          onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent}
        />
      )}
    </div>
  );
}
